// src/components/Layout/MovimientoForm.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
} from '@mui/material';
import { AddCircle, RemoveCircle } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createMovimiento,
  updateMovimiento,
  getMovimientos,
  getBodegas,
  getProductos,
} from '../../services/api';

const MovimientoForm = () => {
  const [movimiento, setMovimiento] = useState({
    tipo: 'entrada', // 'entrada' o 'salida'
    fecha: new Date().toISOString().substr(0, 10), // Formato YYYY-MM-DD
    estado: 'Pendiente',
    idBodega: '',
    usuario: '',
    observacion: '',
    proveedorCliente: '',
    productos: [
      {
        idProducto: '',
        cantidad: 1,
        precioUnitario: 0,
        subtotal: 0,
      },
    ],
  });

  const [bodegas, setBodegas] = useState([]);
  const [productos, setProductos] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { id } = useParams(); // Para edición

  useEffect(() => {
    // Obtener listas de bodegas y productos
    const fetchData = async () => {
      try {
        const [bodegasData, productosData] = await Promise.all([
          getBodegas(),
          getProductos(),
        ]);
        if (bodegasData.success) {
          setBodegas(bodegasData.data);
        }
        if (productosData.success) {
          setProductos(productosData.data);
        }
      } catch (err) {
        setError('Error al obtener datos necesarios.');
      }
    };

    fetchData();

    // Si es edición, obtener los datos del movimiento existente
    if (id) {
      const fetchMovimiento = async () => {
        try {
          const movimientosData = await getMovimientos();
          if (movimientosData.success) {
            const existingMovimiento = movimientosData.data.find(
              (mov) => mov.id === id
            );
            if (existingMovimiento) {
              // Aquí deberías obtener los detalles del movimiento si los tienes
              // Por simplicidad, asumiremos que los detalles están incluidos
              setMovimiento({
                ...existingMovimiento,
                fecha: existingMovimiento.fecha.substr(0, 10), // Formatear fecha
                productos: existingMovimiento.productos || [],
              });
            } else {
              setError('Movimiento no encontrado.');
            }
          }
        } catch (err) {
          setError('Error al obtener el movimiento.');
        }
      };

      fetchMovimiento();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMovimiento({ ...movimiento, [name]: value });
  };

  const handleProductoChange = (index, e) => {
    const { name, value } = e.target;
    const updatedProductos = [...movimiento.productos];
    updatedProductos[index][name] = value;

    // Actualizar el subtotal automáticamente
    if (name === 'cantidad' || name === 'precioUnitario') {
      const cantidad = parseFloat(updatedProductos[index].cantidad) || 0;
      const precioUnitario = parseFloat(updatedProductos[index].precioUnitario) || 0;
      updatedProductos[index].subtotal = cantidad * precioUnitario;
    }

    setMovimiento({ ...movimiento, productos: updatedProductos });
  };

  const handleAddProducto = () => {
    setMovimiento({
      ...movimiento,
      productos: [
        ...movimiento.productos,
        { idProducto: '', cantidad: 1, precioUnitario: 0, subtotal: 0 },
      ],
    });
  };

  const handleRemoveProducto = (index) => {
    const updatedProductos = [...movimiento.productos];
    updatedProductos.splice(index, 1);
    setMovimiento({ ...movimiento, productos: updatedProductos });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validaciones básicas
    if (!movimiento.idBodega) {
      setError('Por favor, selecciona una bodega.');
      return;
    }

    if (movimiento.productos.length === 0) {
      setError('Por favor, agrega al menos un producto al movimiento.');
      return;
    }

    try {
      if (id) {
        // Actualizar movimiento
        const data = await updateMovimiento({ id, ...movimiento });
        if (data.success) {
          navigate('/movimientos');
        } else {
          setError(data.error || 'Error al actualizar el movimiento.');
        }
      } else {
        // Crear nuevo movimiento
        const data = await createMovimiento(movimiento);
        if (data.success) {
          navigate('/movimientos');
        } else {
          setError(data.error || 'Error al crear el movimiento.');
        }
      }
    } catch (err) {
      setError(err.error || 'Error en la operación.');
    }
  };

  return (
    <Container maxWidth="md">
      <Box mt={4} p={4} boxShadow={3}>
        <Typography variant="h5" gutterBottom>
          {id ? 'Editar Movimiento' : 'Crear Movimiento'}
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <form onSubmit={handleSubmit}>
          <FormControl fullWidth margin="normal">
            <InputLabel id="tipo-label">Tipo de Movimiento</InputLabel>
            <Select
              labelId="tipo-label"
              label="Tipo de Movimiento"
              name="tipo"
              value={movimiento.tipo}
              onChange={handleChange}
              required
            >
              <MenuItem value="entrada">Entrada</MenuItem>
              <MenuItem value="salida">Salida</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Fecha"
            type="date"
            name="fecha"
            value={movimiento.fecha}
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
            margin="normal"
            required
          />

          <FormControl fullWidth margin="normal">
            <InputLabel id="estado-label">Estado</InputLabel>
            <Select
              labelId="estado-label"
              label="Estado"
              name="estado"
              value={movimiento.estado}
              onChange={handleChange}
              required
            >
              <MenuItem value="Pendiente">Pendiente</MenuItem>
              <MenuItem value="Completado">Completado</MenuItem>
              <MenuItem value="Cancelado">Cancelado</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel id="bodega-label">Bodega</InputLabel>
            <Select
              labelId="bodega-label"
              label="Bodega"
              name="idBodega"
              value={movimiento.idBodega}
              onChange={handleChange}
              required
            >
              {bodegas.map((bodega) => (
                <MenuItem key={bodega.id} value={bodega.id}>
                  {bodega.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Usuario"
            name="usuario"
            value={movimiento.usuario}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />

          <TextField
            label="Observación"
            name="observacion"
            value={movimiento.observacion}
            onChange={handleChange}
            fullWidth
            margin="normal"
            multiline
            rows={3}
          />

          <TextField
            label="Proveedor/Cliente"
            name="proveedorCliente"
            value={movimiento.proveedorCliente}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />

          <Box mt={4} mb={2}>
            <Typography variant="h6">Productos</Typography>
          </Box>

          {movimiento.productos.map((producto, index) => (
            <Box key={index} mb={2} p={2} border={1} borderRadius={2} borderColor="grey.300">
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel id={`producto-label-${index}`}>Producto</InputLabel>
                    <Select
                      labelId={`producto-label-${index}`}
                      label="Producto"
                      name="idProducto"
                      value={producto.idProducto}
                      onChange={(e) => handleProductoChange(index, e)}
                      required
                    >
                      {productos.map((prod) => (
                        <MenuItem key={prod.id} value={prod.id}>
                          {prod.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    label="Cantidad"
                    type="number"
                    name="cantidad"
                    value={producto.cantidad}
                    onChange={(e) => handleProductoChange(index, e)}
                    inputProps={{ min: 1 }}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    label="Precio Unitario"
                    type="number"
                    name="precioUnitario"
                    value={producto.precioUnitario}
                    onChange={(e) => handleProductoChange(index, e)}
                    inputProps={{ min: 0 }}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    label="Subtotal"
                    type="number"
                    name="subtotal"
                    value={producto.subtotal}
                    InputProps={{
                      readOnly: true,
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <IconButton
                    color="secondary"
                    onClick={() => handleRemoveProducto(index)}
                    disabled={movimiento.productos.length === 1}
                  >
                    <RemoveCircle />
                  </IconButton>
                  {index === movimiento.productos.length - 1 && (
                    <IconButton color="primary" onClick={handleAddProducto}>
                      <AddCircle />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            </Box>
          ))}

          <Box mt={4}>
            <Button type="submit" variant="contained" color="primary" fullWidth>
              {id ? 'Actualizar Movimiento' : 'Crear Movimiento'}
            </Button>
          </Box>
        </form>
      </Box>
    </Container>
  );
};

export default MovimientoForm;
